/**
 * Retry a photo request while the server says it is busy.
 *
 * The expensive public endpoints can answer 503 with Retry-After when
 * Rekognition is throttling or a downstream resource is momentarily exhausted.
 * That is a "wait a moment", not a failure: the athlete's photos are there and
 * we simply could not look right then. Without this the finder showed an error
 * and sent them back to the camera, which reads as "your photos are not here"
 * and is the single worst thing we could tell someone who just paid to be at
 * the event.
 *
 * Deliberately does NOT retry 429. A 429 means this person has personally made
 * too many requests inside a rolling window, and waiting five seconds does not
 * clear a sixty-second window: retrying just spends their allowance faster and
 * delays an honest message. Only 503 (we are busy) is worth waiting through.
 */

export interface BusyRetryState {
  /** 1 for the first retry, 2 for the second, and so on. */
  attempt: number;
  /** How long we are about to wait, in milliseconds. */
  waitMs: number;
}

export interface BusyRetryOptions {
  /** Total attempts including the first. */
  maxAttempts?: number;
  /** Give up once this much wall time has passed, so nobody waits forever. */
  maxTotalMs?: number;
  /** Called before each wait, so the UI can say what is happening. */
  onWaiting?: (state: BusyRetryState) => void;
  /** Injectable for tests. */
  sleep?: (ms: number) => Promise<void>;
  random?: () => number;
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * How long to wait before the next attempt. Honours the server's Retry-After
 * when it sends one, else backs off exponentially.
 *
 * Jitter matters more than it looks: a crowd that all hit a busy server retry
 * in lockstep and arrive together again, turning one spike into several. The
 * random spread breaks that up.
 */
export function nextBackoffMs(
  attempt: number,
  retryAfterHeader: string | null,
  random: () => number = Math.random,
): number {
  const retryAfter = Number(retryAfterHeader);
  const base = Number.isFinite(retryAfter) && retryAfter > 0
    ? Math.min(retryAfter * 1000, 10_000)
    : Math.min(2 ** attempt * 500, 8_000);
  return Math.round(base + random() * 500);
}

export async function fetchThroughBusy(
  input: string,
  init: RequestInit,
  options: BusyRetryOptions = {},
): Promise<Response> {
  const {
    maxAttempts = 4,
    maxTotalMs = 30_000,
    onWaiting,
    sleep = defaultSleep,
    random = Math.random,
  } = options;

  const startedAt = Date.now();
  let response = await fetch(input, init);

  for (let attempt = 1; attempt < maxAttempts; attempt++) {
    if (response.status !== 503) return response;

    const waitMs = nextBackoffMs(attempt, response.headers.get('Retry-After'), random);
    // Stop before a wait that would take us past the budget, rather than
    // starting it and blowing through: a caller who has been told 30 seconds
    // should not be kept for 38.
    if (Date.now() - startedAt + waitMs > maxTotalMs) return response;

    onWaiting?.({ attempt, waitMs });
    await sleep(waitMs);
    response = await fetch(input, init);
  }

  return response;
}
